import React, { useState, lazy, Suspense, useCallback } from 'react'
import { css } from '@linaria/core'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useOverlay, Ellipsis, ListItem, List, colors, Paragraph, Button } from '@fs/zion-ui'
import { useQueryParams } from '@fs/zion-router'
import { DocumentPhoto, LogoFamilysearch, PlaceWorld } from '@fs/zion-icon'
import { getNameRelatedData } from '@fs/zion-person-service'
import { trackEvent } from '@fs/zion-analytics'
import InfoCard from '../info-card/PersonInfoCard'

import CountHeader from '../count-header/CountHeader'
import svg from './Sources.svg'

const MediaOverlay = lazy(() => import('../media-overlay/MediaOverlay'))
const SourceRequiresLoginOverlay = lazy(() => import('./SourceRequiresLoginOverlay'))

const viewAllCss = css`
  margin-top: 8px;
`

const noSourcesCss = css`
  margin-top: 12px;
  text-align: center;
`

const noSourcesTextCss = css`
  text-align: center;
  padding: 8px 0 4px 0;
`

const noSourcesImgCss = css`
  height: 170px;
  max-width: 100%;
`

const iconSpacingCss = css`
  padding: 0 8px;
`

const isPublicSource = (source) => {
  return (
    (!source.sourceImageLink && !source.sourceThumbnailLink) ||
    (source.sourceImageLink && source.imagePublic) ||
    (!source.sourceImageLink && source.sourceThumbnailLink && source.thumbnailPublic)
  )
}

const findSelectedSource = (sources, query) => {
  return sources.find((source) => {
    if (query?.sourceUrl) return query?.sourceUrl?.includes(source.sourceLink) && isPublicSource(source)

    return isPublicSource(source)
  })
}

function GetIcon({ sourceType }) {
  switch (sourceType) {
    case 'USER_UPLOADED':
      return (
        <div className={iconSpacingCss}>
          <DocumentPhoto color={`${colors.gray100}`} />
        </div>
      )
    case 'FSREADONLY':
    case 'IGI':
    case 'ASIAN_DATA_ENTRY':
    case 'LEGACY':
      return (
        <div className={iconSpacingCss}>
          <LogoFamilysearch color={`${colors.gray100}`} />
        </div>
      )
    default:
      return (
        <div className={iconSpacingCss}>
          <PlaceWorld color={`${colors.gray100}`} />
        </div>
      )
  }
}

function DisplayedSource({
  source,
  sourceLink,
  baseUrl,
  setSelectedSource,
  sourcesOverlay,
  restricted,
  requireLoginOverlay,
}) {
  const [t] = useTranslation()
  const Icon = useCallback(() => <GetIcon sourceType={source.sourceType} />, [source.sourceType])

  let url
  if (baseUrl) {
    url = `${baseUrl}/tree/sources/viewedit/${source.sourceUri}`
  } else {
    url = `/tree/sources/viewedit/${source.sourceUri}`
  }
  return (
    <ListItem
      key={source.sourceUri}
      onClick={() => {
        if (sourceLink) {
          setSelectedSource(source)
          trackEvent({ link_name: 'Sources: Opened Source Page' })
        } else if (isPublicSource(source)) {
          setSelectedSource(source)
          trackEvent({ link_name: 'Sources: Source' })
          sourcesOverlay.handleClick()
        } else if (!isPublicSource(source) && !restricted) {
          setSelectedSource(source)
          trackEvent({ link_name: 'Sources: Source Requires Login' })
          requireLoginOverlay.handleClick()
        }
      }}
      to={sourceLink ? url : undefined}
      external
      target={sourceLink ? '_blank' : '_self'}
      data-testid="source-item"
      primaryText={<Ellipsis lineClamp={2}>{source.title}</Ellipsis>}
      startElement={
        source.sourceThumbnailLink && source.thumbnailPublic ? (
          <ListItem.Image size="sm" alt="" src={source.sourceThumbnailLink} />
        ) : (
          <ListItem.Icon Icon={Icon} />
        )
      }
      aria-label={t('info-cards.view-source-image.aria-label')}
    />
  )
}

export default function SourcesCard({
  sources = [],
  sourcesCount = sources.length,
  person,
  restricted = false,
  baseUrl,
  loggedIn = false,
  ...props
}) {
  const [t] = useTranslation()
  const DISPLAY_MAX = 3
  const displayedSources = sources.slice(0, DISPLAY_MAX)

  const { name } = person
  const { shortName } = getNameRelatedData(person)

  const { query } = useQueryParams()
  const requireLoginOverlay = useOverlay()
  const [selectedSource, setSelectedSource] = useState(findSelectedSource(sources, query))
  const sourcesOverlay = useOverlay({ initiallyOpen: !!setInitiallyOpen() })
  const zeroSources = sources.length === 0
  const sourceLink = !!loggedIn

  const viewAllUrl = baseUrl ? `${baseUrl}/tree/person/sources/${person.id}` : `/sources/${person.id}`

  function setInitiallyOpen() {
    return query?.sourceUrl ? sources.find((source) => query?.sourceUrl?.includes(source.sourceLink)) : undefined
  }

  return (
    <InfoCard heading={<CountHeader title={t('info-cards.sources.header')} count={sourcesCount} />} {...props}>
      {/* Source List */}
      {displayedSources && (
        <List>
          {displayedSources.map((source) => (
            <DisplayedSource
              key={source.sourceUri}
              {...{ source, sourceLink, baseUrl, setSelectedSource, sourcesOverlay, restricted, requireLoginOverlay }}
            />
          ))}
        </List>
      )}

      {/* No Sources Graphic & Text */}
      {zeroSources && (
        <div className={noSourcesCss}>
          <img
            src={svg}
            alt={t('info-cards.sources.header')}
            className={noSourcesImgCss}
            data-testid="no-sources-img"
          />
        </div>
      )}

      {/* Something about the sources overlay messes up the calculations made to build the masonry layout. */}
      {/* So this has to be put behind this check. This check actually makes the lazy-loading work as intended anyway. */}
      {/* Sources Overlay */}
      {sourcesOverlay.isOpen && (
        <Suspense fallback="">
          <MediaOverlay
            {...sourcesOverlay}
            title={name}
            media={sources}
            mediaType="source"
            mediaCount={sourcesCount}
            selectedMedia={selectedSource}
            setSelectedMedia={setSelectedSource}
            restricted={restricted}
            person={person}
            loggedIn={loggedIn}
            baseUrl={baseUrl}
          />
        </Suspense>
      )}
      {requireLoginOverlay.isOpen && (
        <Suspense fallback="">
          <SourceRequiresLoginOverlay
            {...requireLoginOverlay}
            title={name}
            person={person}
            baseUrl={baseUrl}
            source={selectedSource}
          />
        </Suspense>
      )}

      {/* View All Button */}
      {!zeroSources && (
        <div className={viewAllCss}>
          <Button
            data-testid="sources-view-all"
            linkName="Sources: View All"
            onClick={(event) => {
              setSelectedSource(sources.find(isPublicSource))
              if (!loggedIn) {
                sourcesOverlay.handleClick(event)
              }
            }}
            to={loggedIn ? viewAllUrl : undefined}
          >
            {t('info-cards.view-all.action')}
          </Button>
        </div>
      )}

      {/* No sources */}
      {sources.length === 0 && (
        <div className={noSourcesTextCss}>
          <Paragraph size="xs">{t('info-cards.no-sources.description', { shortName })}</Paragraph>
        </div>
      )}
    </InfoCard>
  )
}

// Stryker disable all
SourcesCard.propTypes = {
  /** Sources to display tiles for. */
  sources: PropTypes.arrayOf(PropTypes.shape({})),

  /** Person object for the person who's sources are being displayed. */
  person: PropTypes.shape({}),
}
